import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { productsApi } from "../api";
import { SUPPORTED_LANGUAGES, useLanguage } from "../context/LanguageContext";

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

export default function Header() {
  const { language, setLanguage, pathFor, t } = useLanguage();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    productsApi.categories(language).then((data) => active && setCategories(data)).catch(() => active && setCategories([]));
    return () => { active = false; };
  }, [language]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const switchLanguage = (nextLanguage) => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (SUPPORTED_LANGUAGES.some(({ code }) => code === segments[0])) segments[0] = nextLanguage;
    else segments.unshift(nextLanguage);
    setLanguage(nextLanguage);
    navigate(`/${segments.join("/")}${location.pathname.endsWith("/") ? "/" : ""}${location.search}`);
  };

  return (
    <header className="site-header">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="header-main container-wide">
        <Link className="brand" to={pathFor("/")} aria-label="Eco Bambù home">
          <img src="/img/long_logo.png" alt="Eco Bambù" />
        </Link>
        {user && (
          <Link className="mobile-cart-link" to={pathFor("/cart/")} aria-label={t("cart")}><CartIcon /></Link>
        )}
        <button
          className={`menu-toggle${menuOpen ? " is-active" : ""}`}
          type="button"
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span /> <span /> <span />
          <span className="sr-only">Menu</span>
        </button>
        <nav id="primary-navigation" className={menuOpen ? "primary-nav is-open" : "primary-nav"}>
          <NavLink to={pathFor("/about/")}>{t("about")}</NavLink>
          <NavLink to={pathFor("/category/")}>{t("products")}</NavLink>
          {categories.length > 0 && (
            <div className="mobile-nav-categories">
              {categories.map((cat) => (
                <Link key={cat.id} to={`${pathFor("/category/")}?category=${cat.id}`}>{cat.name}</Link>
              ))}
            </div>
          )}
          <NavLink to={pathFor("/contact/")}>{t("contact")}</NavLink>
          <div className="mobile-nav-footer">
            <div className="mobile-nav-footer-left">
              {user?.is_staff && <Link to={pathFor("/cms/")}>{t("cms")}</Link>}
              {user ? (
                <Link to={pathFor("/account/personal-data/")}>{t("myAccount")}</Link>
              ) : (
                <Link to={`${pathFor("/account/login/")}?next=${encodeURIComponent(location.pathname)}`}>{t("logIn")}</Link>
              )}
            </div>
            <label className="mobile-lang-select">
              <select value={language} onChange={(event) => switchLanguage(event.target.value)}>
                {SUPPORTED_LANGUAGES.map((item) => (
                  <option value={item.code} key={item.code}>{item.flag} {item.label}</option>
                ))}
              </select>
            </label>
          </div>
        </nav>
        <div className="header-actions">
          <label className="language-select">
            <span className="sr-only">Language</span>
            <select value={language} onChange={(event) => switchLanguage(event.target.value)}>
              {SUPPORTED_LANGUAGES.map((item) => (
                <option value={item.code} key={item.code}>{item.flag} {item.label}</option>
              ))}
            </select>
          </label>
          {user?.is_staff && (
            <Link className="cms-link" to={pathFor("/cms/")}>{t("cms")}</Link>
          )}
          {user && (
            <Link className="icon-link" to={pathFor("/cart/")} aria-label={t("cart")}><CartIcon /></Link>
          )}
          {user ? (
            <Link className="icon-link" to={pathFor("/account/personal-data/")} aria-label={t("myAccount")}><UserIcon /></Link>
          ) : (
            <Link className="icon-link" to={`${pathFor("/account/login/")}?next=${encodeURIComponent(location.pathname)}`} aria-label={t("logIn")}><UserIcon /></Link>
          )}
        </div>
      </div>
      {categories.length > 0 && (
        <nav className="collection-nav" aria-label="Product collections">
          <div className="collection-nav-inner">
            {categories.map((category) => (
              <Link key={category.id} to={`${pathFor("/category/")}?category=${category.id}`}>
                {category.name}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
